import { normalizePhone } from './phone.js';
import { lowerTurkish } from './text.js';

/**
 * The normalizers behind `donors`' four matching columns. Two of them are
 * generated columns in Postgres and the definitions here must stay identical to
 * them: `email_normalized` is `lower(btrim(email))` and `tax_id_normalized` is
 * `regexp_replace(tax_id, '\D', '', 'g')`. The other two are written by the
 * application, because E.164 needs a country and Turkish case folding is not
 * `lower()` — see docs/decisions/0024-donors-dedup-and-merge.md.
 */
const blankToNull = (value: string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

export function normalizeDonorEmail(value: string | null | undefined): string | null {
  const trimmed = blankToNull(value);
  return trimmed === null ? null : trimmed.toLowerCase();
}

export function normalizeDonorTaxId(value: string | null | undefined): string | null {
  const trimmed = blankToNull(value);
  if (trimmed === null) return null;
  const digits = trimmed.replace(/\D/g, '');
  return digits === '' ? null : digits;
}

export function normalizeDonorName(value: string | null | undefined): string | null {
  const trimmed = blankToNull(value);
  return trimmed === null ? null : lowerTurkish(trimmed).replace(/\s+/g, ' ');
}

/**
 * A complete E.164 number, or null — `normalizePhone`, the one normaliser
 * (docs/decisions/0086), under the name 0024's matching columns are written
 * by. `+90 532 …`, `0090 532 …` and a national `0532 …` with `countryIso` of
 * `TR` all give `+90532…`; a national number with no country, a fragment, or a
 * number the country's plan does not allow gives null. The existence lookup of
 * 0024 depends on this being exact-or-nothing: a partial value must be
 * refused, never matched loosely.
 */
export function normalizeDonorPhone(
  value: string | null | undefined,
  countryIso?: string | null,
): string | null {
  return normalizePhone(value, countryIso);
}

export const DONOR_MATCH_SIGNALS = ['tax_id', 'phone', 'email', 'name'] as const;
export type DonorMatchSignal = (typeof DONOR_MATCH_SIGNALS)[number];

/**
 * A tax id, a phone number or an email address each identifies a donor on its
 * own. A name never does: PRD §5 lists `ad-soyad` among the match rules, and
 * treating it as one merges the two unrelated Mehmet Yilmazes that any donor
 * list of this size contains. It ranks; it does not decide.
 */
export const DECISIVE_DONOR_MATCH_SIGNALS: readonly DonorMatchSignal[] = [
  'tax_id',
  'phone',
  'email',
];

const SIGNAL_WEIGHTS: Record<DonorMatchSignal, number> = {
  tax_id: 100,
  phone: 90,
  email: 80,
  name: 20,
};

export type DonorMatchFields = {
  taxIdNormalized?: string | null;
  phoneE164?: string | null;
  emailNormalized?: string | null;
  nameNormalized?: string | null;
};

export type DonorMatchResult = {
  score: number;
  signals: readonly DonorMatchSignal[];
  isDecisive: boolean;
};

const SIGNAL_FIELDS: Record<DonorMatchSignal, keyof DonorMatchFields> = {
  tax_id: 'taxIdNormalized',
  phone: 'phoneE164',
  email: 'emailNormalized',
  name: 'nameNormalized',
};

/**
 * Scores a candidate against the donor being entered. Both sides must already
 * be normalized; comparing raw values is how a duplicate is missed. A score of
 * zero means nothing matched, and `isDecisive` is what the UI uses to tell a
 * confident suggestion from one the user has to read.
 */
export function scoreDonorMatch(
  query: DonorMatchFields,
  candidate: DonorMatchFields,
): DonorMatchResult {
  const signals = DONOR_MATCH_SIGNALS.filter((signal) => {
    const field = SIGNAL_FIELDS[signal];
    const left = query[field];
    const right = candidate[field];
    return Boolean(left) && left === right;
  });

  return {
    score: signals.reduce((total, signal) => total + SIGNAL_WEIGHTS[signal], 0),
    signals,
    isDecisive: signals.some((signal) => DECISIVE_DONOR_MATCH_SIGNALS.includes(signal)),
  };
}
